import { z } from 'zod';
import { formAnswersSchema } from './form';
export const eventStatusSchema = z.enum(['draft', 'published', 'closed', 'cancelled']);
export const registrationStatusSchema = z.enum([
    'registered', 'attended', 'donated', 'rejected', 'no_show', 'cancelled',
]);
export const EVENT_STATUS_LABELS = {
    draft: 'Draf',
    published: 'Dipublikasikan',
    closed: 'Ditutup',
    cancelled: 'Dibatalkan',
};
export const REGISTRATION_STATUS_LABELS = {
    registered: 'Terdaftar',
    attended: 'Datang',
    donated: 'Sudah donor',
    rejected: 'Ditolak di lokasi',
    no_show: 'Tidak datang',
    cancelled: 'Dibatalkan',
};
/**
 * Legal drive status transitions.
 *
 * `closed` and `cancelled` are terminal. Reopening a cancelled drive would let
 * registrations made against it come back to life without anybody re-checking whether
 * the donors are still eligible, and a closed drive's bags are already in stock.
 *
 * A published drive whose details change is PATCHed, not un-published: donors have
 * already been told it exists.
 */
export const EVENT_TRANSITIONS = {
    draft: ['published', 'cancelled'],
    published: ['closed', 'cancelled'],
    closed: [],
    cancelled: [],
};
/** Legal registration status transitions. Used by both server and client. */
export const REGISTRATION_TRANSITIONS = {
    registered: ['attended', 'no_show', 'cancelled'],
    attended: ['donated', 'rejected'],
    donated: [],
    rejected: [],
    no_show: [],
    cancelled: [],
};
/** Base object kept separate so updates can use .partial() without its refinements. */
const eventBaseSchema = z.object({
    branchId: z.string().uuid(),
    code: z.string().trim().min(2).max(32)
        .regex(/^[A-Za-z0-9-]+$/, 'Kode hanya boleh huruf, angka, dan tanda hubung'),
    name: z.string().trim().min(3, 'Nama event minimal 3 karakter').max(200),
    description: z.string().trim().max(2000).optional().nullable(),
    imageUrl: z.string().trim().max(1000).optional().nullable(),
    venueName: z.string().trim().max(200).optional().nullable(),
    address: z.string().trim().max(500).optional().nullable(),
    city: z.string().trim().max(200).optional().nullable(),
    latitude: z.coerce.number().min(-90).max(90).optional().nullable(),
    longitude: z.coerce.number().min(-180).max(180).optional().nullable(),
    startsAt: z.string().datetime({ offset: true }),
    endsAt: z.string().datetime({ offset: true }),
    quota: z.coerce.number().int().min(1).max(10000).optional().nullable(),
    /** The assessment form version in force. Required before a drive is published. */
    formVersionId: z.string().uuid().optional().nullable(),
});
export const eventInputSchema = eventBaseSchema.superRefine((v, ctx) => {
    if (new Date(v.endsAt) <= new Date(v.startsAt)) {
        ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'Waktu selesai harus setelah waktu mulai.' });
    }
    if ((v.latitude == null) !== (v.longitude == null)) {
        ctx.addIssue({ code: 'custom', path: ['longitude'], message: 'Lintang dan bujur harus diisi bersamaan.' });
    }
});
/**
 * Updates use the base object without cross-field refinements, because a partial PATCH
 * does not always send both halves of a pair. Consistency is still guarded by CHECK
 * constraints in the database.
 */
export const eventUpdateSchema = eventBaseSchema.partial();
/** Donor-facing drive search: date filter plus nearest-first ordering. */
export const eventSearchSchema = z.object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    latitude: z.coerce.number().min(-90).max(90).optional(),
    longitude: z.coerce.number().min(-180).max(180).optional(),
    radiusKm: z.coerce.number().min(1).max(500).default(50),
    branchId: z.string().uuid().optional(),
}).superRefine((v, ctx) => {
    if ((v.latitude == null) !== (v.longitude == null)) {
        ctx.addIssue({ code: 'custom', path: ['longitude'], message: 'Lintang dan bujur harus diisi bersamaan.' });
    }
});
export const eventRegisterSchema = z.object({
    answers: formAnswersSchema,
});
/**
 * What a drive still needs before it may be published. The database enforces this with
 * the `event_published_is_complete` CHECK; this is the same rule stated for the reader,
 * so the UI can explain the disabled button instead of waiting for a 422.
 */
export function publishBlockers(e) {
    const missing = [];
    if (e.latitude === null)
        missing.push('koordinat lokasi');
    if (!e.formVersionId)
        missing.push('form asesmen');
    return missing;
}
